import { AreaUnitEnum } from '@search/graphql-typings';
import { declensionByNumber } from '../declensionByNumber';
import { formatLocaleNumber } from '../formatNumber';
import { Area } from './types';

/**
 * Возвращает русскоязычную запись единиц площади
 * @param {Area} area
 * @param {number} area.value
 * @param {AreaUnitEnum} area.unit
 * @returns {string}
 * @example
 * formatAreaUnit({ value: 1, unit: AreaUnitEnum.Are }) -> 'сотка'
 * formatAreaUnit({ value: 5, unit: AreaUnitEnum.Hectare }) -> 'гектаров'
 * formatAreaUnit({ value: 100, unit: AreaUnitEnum.Meter }) -> 'м²'
 */
export const formatAreaUnit = ({
    value,
    unit
}: Area): string => {
    const integer = String(value).includes('.') ? 2 : value;

    switch (unit) {
        case AreaUnitEnum.Are:
            return declensionByNumber(integer, [ 'сотка', 'сотки', 'соток' ]);
        case AreaUnitEnum.Hectare:
            return declensionByNumber(integer, [ 'гектар', 'гектара', 'гектаров' ]);
        default:
            return 'м²';
    }
};

/**
 * Возвращает русскоязычную запись единиц площади в винительном падеже,
 * как например, в предложении "за 5 соток"
 * @param {Area} area
 * @param {number} area.value
 * @param {AreaUnitEnum} area.unit
 * @returns {string}
 * @example
 * formatAreaUnitFor({ value: 1, unit: AreaUnitEnum.Are }) -> 'сотку'
 * formatAreaUnitFor({ value: 5, unit: AreaUnitEnum.Hectare }) -> 'гектаров'
 * formatAreaUnitFor({ value: 100, unit: AreaUnitEnum.Meter }) -> 'м²'
 */
export const formatAreaUnitFor = ({
    value,
    unit
} : Area): string => unit === AreaUnitEnum.Are ?
    declensionByNumber(value, [ 'сотку', 'сотки', 'соток' ]) :
    formatAreaUnit({ value, unit });

/**
 * Возвращает человекопонятную запись площади
 * @param {Area} area
 * @param {boolean} [hasUnit = false]
 * @returns {string}
 * @example
 * formatArea({ value: 2.34, unit: AreaUnitEnum.Hectare }) -> '2,3 гектара'
 */
export const formatArea = (area: Area, hasUnit: boolean = true): string => {
    const newValue = parseFloat(area.value.toFixed(1));
    const valueString = formatLocaleNumber(newValue, 1);

    return `${valueString}${hasUnit ? ` ${formatAreaUnit({
        value: newValue,
        unit: area.unit
    })}` : ''}`;
};

/**
 * Возвращает человекопонятную запись диапазона площадей
 * @param {Object} range
 * @param {Area} range.minArea
 * @param {Area} range.maxArea
 * @returns {string}
 * @example
 * formatAreaRange(
 *     minArea: {
 *         value: 1.23,
 *         unit: AreaUnitEnum.Are
 *     },
 *     maxArea: {
 *         value: 2.34,
 *         unit: AreaUnitEnum.Are
 *     }
 * ) -> '1,2 - 2,3 сотки'
 */
export const formatAreaRange = ({ minArea, maxArea }: {
    minArea: Area;
    maxArea: Area;
}): string => `${formatArea(minArea, false)} - ${formatArea(maxArea)}`;
